/* eslint-disable react/prop-types */
import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { CaseWhenBenefit, CaseWhenBenefitMonth } from "services/GraphQl";
import FormattedCurrency from "components/WhenBenefit/FormattedCurrency";
import moment from "moment";

interface MonthlyBenefitTableProps {
  whenBenefit: CaseWhenBenefit;
}

const MonthlyWhenBenefitsTable: React.FC<MonthlyBenefitTableProps> = ({ whenBenefit }) => {
  const months = whenBenefit.months || [];
  const detail = whenBenefit.detail || {};

  // Calculate totals
  const totalPending = months.reduce((sum, month) => sum + (month.requested || 0), 0);
  const totalReimbursed = months.reduce((sum, month) => sum + (month.reimbursedAmount || 0), 0);
  const totalExpired = months.reduce((sum, month) => sum + (month.expired || 0), 0);

  // Calculate remaining available amount (original amount - pending - approved)
  const totalRemainingAvailable = months.reduce((sum, month) => {
    const monthApproved =
      whenBenefit.reimbursementRequests
        ?.filter((req) => req.status === "APPROVED" && req.coverageMonths?.includes(month.month))
        .reduce((reqSum, req) => {
          const monthsCount = req.coverageMonths?.length || 1;
          return reqSum + (req.approvedAmount || req.amount || 0) / monthsCount;
        }, 0) || 0;
    
    const remainingForMonth = (month.amount || 0) - (month.requested || 0) - monthApproved;
    return sum + Math.max(0, remainingForMonth); // Ensure non-negative
  }, 0);

  const formatMonth = (monthStr: string) => {
    return moment(monthStr, "YYYY-MM").format("MMMM YYYY");
  };

  const formatDate = (date: string | Date | moment.Moment | null | undefined) => {
    if (!date) return "—";
    
    // Handle string dates from database
    if (typeof date === 'string') {
      // Try parsing as ISO string first, then as a general date string
      const parsedDate = moment(date);
      if (parsedDate.isValid()) {
        return parsedDate.format("MMM DD, YYYY");
      } else {
        console.warn("Invalid date string:", date);
        return "Invalid Date";
      }
    }
    
    // Handle Date objects or Moment objects
    return moment(date).format("MMM DD, YYYY");
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", p: 3 }}>
      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #e0e0e0",
          borderRadius: 2
        }}
      >
        <Table>
          {/* Header Section inside table */}
          <TableHead>
            <TableRow>
              <TableCell 
                colSpan={7} 
                sx={{ 
                  bgcolor: "white",
                  borderBottom: "1px solid #e0e0e0",
                  py: 3,
                  px: 3
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, fontSize: "1.25rem" }}>
                  When Benefit Provided (Monthly Installments)
                </Typography>
                
                {/* Summary Info Row */}
                <Box sx={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Total When Benefit Amount
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "black" }}>
                      <FormattedCurrency value={detail.total || 0} />
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Amount Per Month
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "black" }}>
                      <FormattedCurrency value={months.length > 0 ? (detail.total || 0) / months.length : 0} />
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Number of Months
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "black" }}>
                      {whenBenefit.monthsToAllocate || months.length}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
            
            {/* Column Headers */}
            <TableRow sx={{ bgcolor: "#f8f9fa" }}>
              <TableCell sx={{ fontWeight: "600", color: "#6b7280", fontSize: "0.875rem", py: 2 }}>
                Eligibility Period
              </TableCell>
              <TableCell sx={{ fontWeight: "600", color: "#6b7280", fontSize: "0.875rem", py: 2 }}>
                Amount Available
              </TableCell>
              <TableCell sx={{ fontWeight: "600", color: "#6b7280", fontSize: "0.875rem", py: 2 }}>
                Amount Pending
              </TableCell>
              <TableCell sx={{ fontWeight: "600", color: "#6b7280", fontSize: "0.875rem", py: 2 }}>
                Amount Reimbursed
              </TableCell>
              <TableCell sx={{ fontWeight: "600", color: "#6b7280", fontSize: "0.875rem", py: 2 }}>
                Expired Amount
              </TableCell>
              <TableCell sx={{ fontWeight: "600", color: "#6b7280", fontSize: "0.875rem", py: 2 }}>
                Expiration Date
              </TableCell>
            </TableRow>
          </TableHead>
          
          <TableBody>
            {months.map((month: CaseWhenBenefitMonth) => {
              // Calculate approved amount for this specific month from reimbursement requests
              const monthApproved =
                whenBenefit.reimbursementRequests
                  ?.filter((req) => req.status === "APPROVED" && req.coverageMonths?.includes(month.month))
                  .reduce((sum, req) => {
                    const monthsCount = req.coverageMonths?.length || 1;
                    return sum + (req.approvedAmount || req.amount || 0) / monthsCount;
                  }, 0) || 0;

              // Calculate remaining available amount for this month
              const remainingAvailable = Math.max(0, (month.amount || 0) - (month.requested || 0) - monthApproved);

              return (
                <TableRow 
                  key={month.month} 
                  sx={{ 
                    "&:hover": { bgcolor: "#f9fafb" },
                    borderBottom: "1px solid #f3f4f6"
                  }}
                >
                  <TableCell sx={{ py: 2.5, fontWeight: "500" }}>
                    {formatMonth(month.month)}
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    <FormattedCurrency value={remainingAvailable} />
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    <FormattedCurrency value={month.requested || 0} />
                  </TableCell>
                
                  <TableCell sx={{ py: 2.5 }}>
                    <FormattedCurrency value={month.reimbursedAmount || 0} />
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    {month.expired ? <FormattedCurrency value={month.expired} /> : "—"}
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    {month.deadline ? formatDate(month.deadline) : "—"}
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Totals Row */}
            <TableRow sx={{ bgcolor: "#f8f9fa", borderTop: "2px solid #e5e7eb" }}>
              <TableCell sx={{ fontWeight: "bold", py: 2.5, fontSize: "0.95rem" }}>
                Total
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", py: 2.5 }}>
                <FormattedCurrency value={totalRemainingAvailable} />
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", py: 2.5 }}>
                <FormattedCurrency value={totalPending} />
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", py: 2.5 }}>
                <FormattedCurrency value={totalReimbursed} />
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", py: 2.5 }}>
                <FormattedCurrency value={totalExpired} />
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", py: 2.5 }}>
                
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MonthlyWhenBenefitsTable;
